import React, { useCallback, useEffect, useState } from "react";
import api from "../../api";
import { useAuth } from "../../context/AuthContext";
import "./Library.css";
const blank = {
  title: "",
  description: "",
  document_number: "",
  category_id: "",
  visibility: "all_members",
  branch_id: "",
  effective_date: "",
  expiry_date: "",
  document: null,
};
export default function Library() {
  const { hasPermission } = useAuth(),
    manage =
      hasPermission("documents.create") || hasPermission("documents.update"),
    canIndex = hasPermission("ai.manage");
  const [docs, setDocs] = useState([]),
    [categories, setCategories] = useState([]),
    [branches, setBranches] = useState([]),
    [form, setForm] = useState(blank),
    [show, setShow] = useState(false),
    [search, setSearch] = useState(""),
    [category, setCategory] = useState(""),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const requests = [
        api.getDocuments({ search, category_id: category }),
        api.getDocumentCategories(),
      ];
      if (manage) requests.push(api.getBranchOptions());
      const [d, c, b] = await Promise.all(requests);
      setDocs(d.data || []);
      setCategories(c.data || []);
      setBranches(b?.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [search, category, manage]);
  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);
  const upload = async (e) => {
    e.preventDefault();
    try {
      const r = await api.uploadDocument(form);
      setNotice(r.message);
      setForm(blank);
      setShow(false);
      await load();
    } catch (x) {
      setError(x.message);
    }
  };
  const transition = async (id, action) => {
    try {
      const r = await api.transitionDocument(id, action);
      setNotice(r.message);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };
  const indexForAi = async (id) => {
    try {
      setError(""); setNotice("Reading and indexing document…");
      const response = await api.indexDocumentForAi(id);
      setNotice(`${response.message} ${response.data?.chunks || 0} knowledge chunks created.`);
    } catch (e) { setError(e.message); }
  };
  return (
    <div className="document-page">
      <header className="document-hero">
        <div>
          <span>TAREWU KNOWLEDGE CENTRE</span>
          <h1>Document Library</h1>
          <p>
            Controlled policies, agreements, meeting records, forms and training
            materials.
          </p>
        </div>
        {manage && (
          <button onClick={() => setShow(!show)}>
            {show ? "Close" : "+ Upload document"}
          </button>
        )}
      </header>
      {error && <div className="doc-alert error">{error}</div>}
      {notice && <div className="doc-alert success">{notice}</div>}
      {manage && show && (
        <form className="document-form" onSubmit={upload}>
          <h2>New controlled document</h2>
          <label>
            Title *
            <input
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </label>
          <label>
            Document number
            <input
              value={form.document_number}
              onChange={(e) =>
                setForm({ ...form, document_number: e.target.value })
              }
            />
          </label>
          <label>
            Category *
            <select
              required
              value={form.category_id}
              onChange={(e) =>
                setForm({ ...form, category_id: e.target.value })
              }
            >
              <option value="">Select category</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Visibility
            <select
              value={form.visibility}
              onChange={(e) => setForm({ ...form, visibility: e.target.value })}
            >
              <option value="all_members">All members</option>
              <option value="branch">Specific branch</option>
              <option value="staff">Staff only</option>
              <option value="private">Private administrators</option>
            </select>
          </label>
          {form.visibility === "branch" && (
            <label>
              Branch *
              <select
                required
                value={form.branch_id}
                onChange={(e) =>
                  setForm({ ...form, branch_id: e.target.value })
                }
              >
                <option value="">Select branch</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.code} — {b.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label>
            Effective date
            <input
              type="date"
              value={form.effective_date}
              onChange={(e) =>
                setForm({ ...form, effective_date: e.target.value })
              }
            />
          </label>
          <label>
            Expiry date
            <input
              type="date"
              value={form.expiry_date}
              onChange={(e) =>
                setForm({ ...form, expiry_date: e.target.value })
              }
            />
          </label>
          <label className="wide">
            Description
            <textarea
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />
          </label>
          <label className="wide file-drop">
            Document file *
            <input
              required
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
              onChange={(e) =>
                setForm({ ...form, document: e.target.files?.[0] || null })
              }
            />
            <small>
              {form.document?.name || "Select a secure document file"}
            </small>
          </label>
          <button>Upload as draft</button>
        </form>
      )}
      <div className="document-tools">
        <input
          placeholder="Search title or document number…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      {loading ? (
        <div className="document-empty">Loading documents…</div>
      ) : docs.length ? (
        <div className="document-grid">
          {docs.map((d) => (
            <article className="document-card" key={d.id}>
              <div className="document-icon">
                {d.mime_type?.includes("pdf") ? "PDF" : "DOC"}
              </div>
              <div className="document-card-body">
                <div>
                  <span>{d.category_name}</span>
                  <span className={`doc-status ${d.status}`}>{d.status}</span>
                </div>
                <h3>{d.title}</h3>
                <p>{d.description || "No description provided."}</p>
                <small>
                  {d.document_number || "Unnumbered"} · Version{" "}
                  {d.current_version} ·{" "}
                  {(Number(d.file_size || 0) / 1024).toFixed(1)} KB
                </small>
              </div>
              <div className="document-actions">
                <button
                  onClick={() =>
                    api
                      .downloadDocument(d.id, d.file_name)
                      .catch((e) => setError(e.message))
                  }
                >
                  Download
                </button>
                {canIndex && d.status === "published" && (
                  <button onClick={() => indexForAi(d.id)}>✦ Index for AI</button>
                )}
                {manage && d.status === "draft" && (
                  <button onClick={() => transition(d.id, "submit")}>
                    Submit review
                  </button>
                )}
                {manage && d.status === "review" && (
                  <button onClick={() => transition(d.id, "publish")}>
                    Publish
                  </button>
                )}
                {manage && d.status === "published" && (
                  <button
                    className="light"
                    onClick={() => transition(d.id, "archive")}
                  >
                    Archive
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="document-empty">No documents found.</div>
      )}
    </div>
  );
}
